import { useEffect } from "react";

import { css } from "@compiled/react";
import { defineMessages, FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { N80, N700 } from "@atlaskit/theme/colors";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    ProductKey
} from "@/components/signup/utils";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

import { useAnalyticsClient } from "../../../../../../../../analytics/wac-analytics/analytics-client";
import MainContainer from "../../components/main-container/main-container";
import { useTipsViewType } from "../../hooks";
import ProgressBar from "../../sections/progress-bar";

import { ProductTipList } from "./components";

const tipImages = {
    tips: "https://wac-cdn.atlassian.com/dam/jcr:db7f2385-5667-42ca-b5c7-0fea0d31de06/app-switcher-jira.svg",
    switcherJiraJsm:
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/JiraJSMProvisioning.svg",
    switcherJsmJira:
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/JSMJiraprovisioning.svg",
    switcherJiraConf:
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/JiraConfluenceprovisioning.svg"
};

const TipsView = () => {
    const { isClaiming, products } = useSearchParameters();

    let image;
    switch (products.join(",")) {
        case `${ProductKey.JIRA_SOFTWARE},${ProductKey.JIRA_SERVICE_MANAGEMENT}`:
            image = tipImages.switcherJiraJsm;
            break;
        case `${ProductKey.JIRA_SERVICE_MANAGEMENT},${ProductKey.JIRA_SOFTWARE}`:
            image = tipImages.switcherJsmJira;
            break;
        case `${ProductKey.JIRA_SOFTWARE},${ProductKey.CONFLUENCE}`:
            image = tipImages.switcherJiraConf;
            break;
        default:
            image = tipImages.tips;
    }

    const type = useTipsViewType();
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        analyticsClient &&
            createAnalyticsEvent({
                eventName: "tipsPageViewed",
                eventComponent: "tipsPage",
                event: "viewed"
            }).fire("signup");
    }, [analyticsClient, createAnalyticsEvent]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={UfoCustomTimingKey.TIPS_VIEW_DWELL_TIME}
        >
            <MainContainer>
                <div css={tipsViewStyles}>
                    <div css={columnStyles}>
                        <div css={itemStyles}>
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={image} height="200px" alt="" />
                        </div>
                    </div>
                    <div css={columnStyles}>
                        <div css={itemStyles}>
                            <FormattedMessage
                                {...TipsView.messages.tips}
                                values={{
                                    isClaiming,
                                    type,
                                    h2: (...chunks) => <h2>{chunks}</h2>,
                                    menu: <MenuIcon />,
                                    ...DEFAULT_VALUES
                                }}
                            />
                        </div>
                        <ProductTipList />
                    </div>
                </div>
                <ProgressBar />
            </MainContainer>
        </UfoCustomTimingWrapper>
    );
};

export default TipsView;

const tipsViewStyles = css({
    display: "flex",
    flexDirection: "row",
    padding: "8px",
    maxWidth: "550px",
    fontSize: "16px",
    color: N80,
    fontWeight: 400,
    letterSpacing: "0.21px",
    textAlign: "left",
    fontFamily: `Charlie Display, -apple-system, BlinkMacSystemFont, Segoe UI,
        Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif`,
    // Tech debt: Adding this disable to unblock commits for GWP-2646

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h2: {
        fontWeight: 500,
        fontSize: "24px",
        color: N700,
        lineHeight: "32px",
        marginBlockStart: 0,
        letterSpacing: "0.3px",
        marginBlockEnd: "8px"
    }
});

const columnStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "justifyContent": "center",
    "margin": "8px",
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p:empty": {
        display: "none"
    }
});

const itemStyles = css({
    margin: "0 8px",
    minWidth: "200px"
});

TipsView.messages = defineMessages({
    tips: {
        id: "WacConfirmationPage.tips",
        defaultMessage: `{ isClaiming, select, 
            true {\n\n<p></p><h2>Explore all of your products</h2>\n\n}
            other {\n\n<p></p><h2>Explore all of your products</h2>\n\n} 
        } {type, select, 
            jira_bundle {Select the menu ({menu}) in the top left to switch between products. Create a project to explore your Jira products' features.} 
            jira_only_bundle {Create a project to explore your Jira products' features.} 
            other {Select the menu ({menu}) in the top left to switch between products.}
        }`
    }
});

const MenuIcon = () => {
    return (
        <span
            css={css({
                display: "inline-flex",
                verticalAlign: "middle",
                paddingBlockEnd: "3px"
            })}
        >
            <svg
                height="14"
                role="presentation"
                css={css({ marginBlockEnd: 0 })}
                viewBox="0 0 24 24"
                width="14"
            >
                <path
                    d="M4 5.01C4 4.451 4.443 4 5.01 4h1.98C7.549 4 8 4.443 8 5.01v1.98C8 7.549 7.557 8 6.99 8H5.01C4.451 8 4 7.557 4 6.99V5.01zm0 6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98C8 13.549 7.557 14 6.99 14H5.01C4.451 14 4 13.557 4 12.99v-1.98zm6-6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98C14 7.549 13.557 8 12.99 8h-1.98C10.451 8 10 7.557 10 6.99V5.01zm0 6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98c0 .558-.443 1.01-1.01 1.01h-1.98c-.558 0-1.01-.443-1.01-1.01v-1.98zm6-6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98C20 7.549 19.557 8 18.99 8h-1.98C16.451 8 16 7.557 16 6.99V5.01zm0 6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98c0 .558-.443 1.01-1.01 1.01h-1.98c-.558 0-1.01-.443-1.01-1.01v-1.98zm-12 6c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98C8 19.549 7.557 20 6.99 20H5.01C4.451 20 4 19.557 4 18.99v-1.98zm6 0c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98c0 .558-.443 1.01-1.01 1.01h-1.98c-.558 0-1.01-.443-1.01-1.01v-1.98zm6 0c0-.558.443-1.01 1.01-1.01h1.98c.558 0 1.01.443 1.01 1.01v1.98c0 .558-.443 1.01-1.01 1.01h-1.98c-.558 0-1.01-.443-1.01-1.01v-1.98z"
                    fill="currentColor"
                    fillRule="evenodd"
                />
            </svg>
        </span>
    );
};
